import {
  useGetRoleOptions,
  useGetRolePermissions,
  useGetUser,
  useUpsertUser,
  useAssignUserTaskGroups,
  usePermissions,
  useGetResource,
  useCompany,
} from '@dispatch/shared/hooks';
import {
  CreateUserPost,
  MultiSelectOption,
  TaskGroup,
  UserData,
  UsersRole,
} from '@dispatch/shared/types';
import { useCurrentUser } from '@dispatch/web/auth';
import { UserDetails, UserForm } from '@dispatch/web/features/users';
import {
  useNotification,
  useNotificationMutationFactory,
} from '@dispatch/web/ui';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

export const UserDetailsPage = () => {
  const { userId: currentUserId, tenantId } = useCurrentUser();
  const { company, roles, taskGroups } = useCompany();
  const { permissions } = usePermissions();
  const params = useParams();
  const userId: string = params['userId'] || '';
  const { data: user, status } = useGetUser(userId);
  const queryClient = useQueryClient();
  const [associatedResource, setAssociatedResource] = React.useState<
    MultiSelectOption | undefined
  >();

  const { data: resource } = useGetResource({
    id: user?.data.resourceId || '',
    company,
    enabled: !associatedResource,
  });

  React.useEffect(() => {
    if (resource?.integrationGUID) {
      setAssociatedResource({
        title: resource?.name,
        value: resource?.integrationGUID,
      } as MultiSelectOption);
    }
  }, [resource]);

  const { notifySuccess } = useNotificationMutationFactory('User', 'update');
  const { notifyError } = useNotification();

  const onSuccess = (response: { success: boolean; message?: string }) => {
    if (response.success) {
      notifySuccess();
      queryClient.invalidateQueries(['userInfo', userId]);
      queryClient.invalidateQueries(['user-permissions', currentUserId]);
    } else {
      notifyError('Error', response.message ?? 'User was not updated');
    }
  };

  const onAssignmentError = () => {
    notifyError('Error', 'Task group was not assigned');
  };

  const { upsertUser, status: mutateStatus } = useUpsertUser(userId, onSuccess);
  const { assignUserTaskGroups, status: assignUserStatus } =
    useAssignUserTaskGroups(userId, onAssignmentError);
  const roleOptions = useGetRoleOptions(roles || []);

  const taskGroupOptions = () => {
    return taskGroups
      ?.filter((taskGroup: TaskGroup) => !!taskGroup.name)
      .map((taskGroup: TaskGroup) => {
        return { title: taskGroup.name, value: taskGroup.id };
      });
  };

  React.useEffect(() => {
    if (user?.data !== undefined && taskGroups !== undefined) {
      user.data.taskGroups = Object.assign(
        {},
        ...taskGroups
          .filter((x) => x.userIds?.includes(userId))
          .map((x) => ({ [x.id]: x.name }))
      );
    }
  }, [taskGroups, user?.data, userId]);

  const handleSave = (values: UserForm) => {
    const resourceIdRaw = values.resourceId as unknown;
    const resourceIdCast = resourceIdRaw as MultiSelectOption;
    const resourceId = resourceIdCast.value;
    const role: UsersRole = values?.role as UsersRole;
    const splitName: string[] = values.name?.split(' ');
    if (!resourceId) {
      setAssociatedResource?.(undefined);
    }

    const postBody: CreateUserPost = {
      id: userId,
      tenantId: tenantId || '' /* needs to not be required */,
      externalId: values.externalId,
      resourceId: resourceId ? String(resourceId) : null,
      name: values.name,
      firstName: splitName?.[0] || '',
      lastName: splitName?.[1] || '',
      email: values.email,
      disabledPermissions: values.disabledPermissions,
      roleId: role.value,
      createdAt: user?.data?.createdAt as string,
      updatedAt: new Date().toISOString(),
      deactivatedAt: null,
    };

    upsertUser(postBody);
    assignUserTaskGroups(Object.keys(values.taskGroups));
  };

  const handleDeactivate = (values: UserData) => {
    if (!values) return;

    const postBody = {
      ...values,
      id: userId,
      externalId: values?.externalId,
      resourceId: values?.resourceId,
      roleId: values?.role?.id,
      updatedAt: new Date().toISOString(),
      deactivatedAt: new Date().toISOString(),
    };

    upsertUser(postBody);
  };

  const handleActivate = (values: UserData) => {
    if (!values) return;

    const postBody = {
      ...values,
      id: userId,
      externalId: values?.externalId,
      resourceId: values?.resourceId,
      roleId: values?.role?.id,
      updatedAt: new Date().toISOString(),
      deactivatedAt: null,
    };

    upsertUser(postBody);
  };

  const handleResetUser = (values: UserData) => {
    if (!values) return;

    const postBody = {
      ...values,
      id: userId,
      externalId: undefined,
      resourceId: values?.resourceId,
      roleId: values.role?.id,
      updatedAt: new Date().toISOString(),
    };

    upsertUser(postBody);
  };

  const statuses = () => {
    if (status === 'loading') {
      return status;
    } else if (mutateStatus === 'loading') {
      return mutateStatus;
    } else if (assignUserStatus === 'loading') {
      return assignUserStatus;
    }

    return 'success';
  };

  const [usersRole, setUsersRole] = React.useState<UsersRole>();
  const [pullPermissions, setPullPermissions] = React.useState<boolean>(false);

  const { data: rolePermissions, status: getRolePermissionsStatus } =
    useGetRolePermissions(
      (usersRole?.data?.permissions as string[])?.map((p: string) => p) || [],
      pullPermissions
    );

  return (
    <UserDetails
      data={user?.data}
      associatedResource={associatedResource}
      setAssociatedResource={setAssociatedResource}
      onSave={handleSave}
      onDeactivate={handleDeactivate}
      onActivate={handleActivate}
      onResetUser={handleResetUser}
      status={statuses()}
      taskGroupOptions={taskGroupOptions() || []}
      roleOptions={roleOptions}
      rolePermissions={rolePermissions || []}
      rolePermissionStatus={getRolePermissionsStatus}
      usersRole={usersRole}
      setUsersRole={setUsersRole}
      pullPermissions={pullPermissions}
      setPullPermissions={setPullPermissions}
      permissions={permissions}
    />
  );
};
